<template>
  <vue-dropzone
    v-show="!getAttribute('statusUploaded')"
    :id="refId"
    :ref="refId"
    :options="dropzoneOptions"
    @vdropzone-complete="afterComplete"
    @vdropzone-sending="sendFile"
    @vdropzone-file-added="fileAdded"
    @vdropzone-drop="drop"
    @vdropzone-error="error"
    @vdropzone-mounted="mounted"
    @vdropzone-upload-progress="progress"
  >
  </vue-dropzone>
</template>
<script>
import { mapState, mapActions } from "pinia";
import { useConfigStore } from "@/stores/modules/config";
import { useUrlsStore } from "@/stores/modules/urls";
import { useUpdatesStore } from "@/stores/modules/updates";
import { useWebproductStore } from "@/stores/modules/webproduct";
import { useUploadsStore } from "@/stores/modules/uploads";
import { useChangeOptionsStore } from "@/stores/modules/changeOptions";
import { useBundleStore } from "@/stores/modules/bundle";
import { useMyAccountStore } from "@/stores/modules/myAccount";
import { usePreview3DStore } from "@/stores/modules/preview3D";
import { usePreflightPitstopStore } from "@/stores/modules/preflightPitstop";
import { usePreflightDataStore } from "@/stores/modules/preflightData";
import { usePersonalizeStore } from "@/stores/modules/personalize";
import { useQuoteStore } from "@/stores/modules/quote";
import { callbackService } from "../../helpers/callbackService";

import { i18n, PREFIX } from "../../i18n.js";

import vue2Dropzone from "vue2-dropzone-vue3";
//import "vue2-dropzone-vue3/dist/vue2Dropzone.min.css";
import axios from "../../axios-auth";

export default {
  components: {
    vueDropzone: vue2Dropzone
  },
  computed: {
    ...mapState(useConfigStore, [
      "getShowUpload",
      "getDisableDeleteUploadAfterConnect"
    ]),
    ...mapState(useWebproductStore, [
      "getSelectionId",
      "getShowPreviewInUpload",
      "getIsPackingEnabled",
      "getIsPreflighPitstop",
      "getNumberOfPP",
      "getNumberOfUploads",
      "getStoreCode",
      "getUploadPersonalizationOneProduct",
      "getSweetAlert"
    ]),
    ...mapState(useChangeOptionsStore, ["getSelectedOption"]),
    ...mapState(useBundleStore, ["getBundleIsActive"]),
    ...mapState(useUploadsStore, [
      "getUploadForProductPart",
      "getFileParameters",
      "getUploadItemId"
    ]),
    ...mapState(useMyAccountStore, [
      "getIsMyAccount",
      "getMyAccountUploadedFiles",
      "getMyAccountUploadPath"
    ]),
    ...mapState(useUpdatesStore, ["getAppSelectionId"]),
    ...mapState(useUrlsStore, ["getUrl"]),
    ...mapState(useQuoteStore, ["getDefaultSelectionUploadsPP"]),
    refId: function () {
      return this.getUploadItemId(this.propsData.code);
    }
  },
  methods: {
    ...mapActions(usePreview3DStore, ["setPreview3DImage"]),
    ...mapActions(usePreflightDataStore, ["setPreflightData"]),
    ...mapActions(useUploadsStore, [
      "setAttributeUploadForProductPart",
      "setAttributeMultiUploadForProductPart",
      "uploadOnDelete"
    ]),
    ...mapActions(usePreflightPitstopStore, [
      "pitstopAddFile",
      "setPreflightInfo"
    ]),
    ...mapActions(useWebproductStore, ["uploadFile", "uploadLoading"]),
    ...mapActions(usePersonalizeStore, [
      "setPersonalizeLoading",
      "setCustomizedProductId"
    ]),
    ...mapActions(useBundleStore, ["setBundleQuantity"]),
    ...mapActions(useQuoteStore, ["setDefaultSelectionUploads"]),
    // this is appended to the url for the thumbnails of the uploaded artwork
    // to avoid the browser caching the image for subsequent uploads
    getAcceptedFiles: function () {
      if (this.excel) {
        return [".csv", ".xlsx", ".xls"];
      }
      const webproductStore = useWebproductStore();
      return webproductStore.getAllowedUploadedFile;
    },
    afterComplete(file) {
      const webproductStore = useWebproductStore();
      this.setAttributes({
        showPreflight: false,
        pitstopReport: false,
        isLoading: false,
        showRearrange: false,
        uploadedFileRelativePath: null,
        loadingProgress: 0
      });
      this.uploadLoading(false);
      this.setPersonalizeLoading(false);

      if (
        Math.ceil(file.size / 1024) >
        webproductStore.getMaxUploadFileSize * 1024
      ) {
        this.getSweetAlert({
          text: i18n.t(
            "File size is too large! Maximum allowed size is {max} MB.",
            {
              max: webproductStore.getMaxUploadFileSize
            }
          ),
          icon: "error"
        });
        return;
      } else if (!file.accepted) {
        this.getSweetAlert({
          text: i18n.t("File is not in accepted format!"),
          icon: "warning"
        });
        return;
      }
      if (file.status == "success") {
        if (file.xhr.response) {
          var response = JSON.parse(file.xhr.response);
          if (response.success) {
            this.afterUploadFile(
              response.file_data[response.temp_filename],
              response.temp_filename,
              response.file_url,
              false
            );
          }
        }
      }
    },
    afterUploadFile: function (file_info, uploadedFile, file_url, mounted) {
      if (!this.getIsMyAccount && !mounted) {
        this.setDefaultSelectionUploads({
          pp: this.propsData.code,
          file: { file_info, uploadedFile, file_url }
        });
      }

      this.uploadFile(this.propsData.code);
      if (this.getIsMyAccount) {
        if (this.getNumberOfPP == this.getNumberOfUploads) {
          callbackService.sendCallback(
            callbackService.callbacks.toggleReadyToConnect,
            true
          );
        }
      }

      if (!this.getUploadPersonalizationOneProduct && !this.getBundleIsActive) {
        this.setCustomizedProductId({ customizedProductId: "" });
      }

      this.setAttribute("uploadedFile", uploadedFile);
      var pdfFile = false;
      if (file_info) {
        if (file_info.extension && file_info.extension == "PDF") {
          pdfFile = true;
          this.setAttribute("showRearrange", true);
          this.setAttribute("uploadedFileRelativePath", file_info.file);
          if (file_info.preflight) {
            this.setAttribute("showPreflight", true);
            var preflightKeys = Object.keys(file_info.preflight);
            var preflightData = [];
            var preflightInfo = {};
            for (var key of preflightKeys) {
              if (
                file_info.preflight[key] &&
                typeof file_info.preflight[key] == "object"
              ) {
                preflightData.push(file_info.preflight[key]);
              } else {
                preflightInfo[key] = file_info.preflight[key];
              }
            }
            this.setPreflightData({
              code: this.propsData.code,
              preflightData
            });
            this.setPreflightInfo({
              code: this.propsData.code,
              preflightInfo
            });
          } else if (this.getIsPreflighPitstop) {
            this.setAttributes({
              pitstopReport: true,
              uploadedFileHash: file_info.hash
            });
            this.pitstopAddFile({
              name: this.getAttribute("uploadedFile"),
              hash: this.getAttribute("uploadedFileHash"),
              product_part: this.propsData.code
            });
          }
          // set it to preflight row line object
        }
        if (this.getShowPreviewInUpload && file_info.preview) {
          this.setAttribute("previewFile", file_info.preview);
          if (file_info.preview_second) {
            this.setAttribute("previewFileSecond", file_info.preview_second);
          } else {
            this.setAttribute("previewFileSecond", "");
          }
          this.setAttribute(
            "previewFileLink",
            this.getAttribute("previewFile")
          );
          if (pdfFile) {
            this.setAttribute(
              "previewFileLink",
              this.getUrl(axios.getPdfUploadPreview) +
                "?" +
                this.getFileParameters(file_info.file)
            );
          }
          // checkf it it's an image. Then point directly to the main file
          if (["PNG", "JPG", "JPEG", "BMP"].includes(file_info.extension)) {
            this.setAttribute("previewFileLink", file_url);
          }
          this.setAttribute("fileUrl", file_url);
          const img = new Image();
          img.src = this.getAttribute("previewFile");
          img.onload = () => {
            this.setAttribute("previewUploaded", true);
          };
          this.statusUploaded = true;
          this.setAttribute("statusUploaded", true);

          if (this.getIsPackingEnabled) {
            // put the preview in 3D
            this.setPreview3DImage([
              this.getAttribute("previewFile"),
              this.getAttribute("previewFileSecond")
            ]);
          }
        } else {
          this.statusUploaded = true;
          this.setAttributes({
            statusUploaded: true,
            fileUrl: file_url,
            uploadedFileRelativePath: file_info.file
          });

          if (this.getBundleIsActive && this.excel && file_info.nr_rows) {
            // set quantity
            this.setBundleQuantity(file_info.nr_rows);
          }
        }
      } else {
        this.error("", "Missing file data information", "");
      }
    },
    sendFile: function (file, xhr, formData) {
      this.setAttribute("isLoading", true);
      this.uploadLoading(true);
      this.prepareDropzoneInput();
      if (this.excel) {
        formData.append("id", "excel_" + this.propsData.code);
        formData.append("is_excel", 1);
      } else {
        formData.append("id", this.propsData.code);
      }
      formData.append("deleteOld", 1);
      if (this.getIsMyAccount && this.getMyAccountUploadPath) {
        formData.append("folder_name", this.getMyAccountUploadPath);
      } else {
        formData.append("folder_name", "tmp." + this.getSelectionId);
      }
      if (this.getAppSelectionId) {
        formData.append("selection", this.getSelectionId);
      }
      if (this.getStoreCode) {
        formData.append("store_code", this.getStoreCode);
      }

      if (this.getIsPackingEnabled) {
        formData.append("packing", 1);
      }
      if (!this.excel) {
        formData.append(
          "selected_pages",
          this.getSelectedOption({ pp: this.propsData.code, option: "pages" })
        );
        formData.append(
          "selected_format",
          this.getSelectedOption({ pp: this.propsData.code, option: "format" })
        );
        formData.append(
          "selected_paper",
          this.getSelectedOption({ pp: this.propsData.code, option: "paper" })
        );
      }
      if (i18n.locale) {
        formData.append("lang", i18n.locale);
      }
    },
    drop: function (event) {
      if (event.dataTransfer && event.dataTransfer.files) {
        if (event.dataTransfer.files.length != 1) {
          this.$refs[this.refId].dropzone.multipleFilesError = true;
          this.getSweetAlert({
            text: i18n.t("Please upload just one file!"),
            icon: "warning"
          });

          return;
        }
      }

      this.setAttribute("isLoading", true);
      //this.uploadLoading(true);
    },

    error: function (file, message, xhr) {
      this.uploadLoading(false);
      this.setAttributes({
        isLoading: false,
        showPreflight: false,
        pitstopReport: false,
        showRearrange: false,
        uploadedFileRelativePath: null
      });
      this.setPersonalizeLoading(false);

      if (message && message.message) {
        this.getSweetAlert({
          text: message.message,
          icon: "error"
        });
      } else if (typeof message == "string") {
        this.getSweetAlert({
          text: message,
          icon: "error"
        });
      }
      this.$refs[this.refId].removeAllFiles();
    },

    fileAdded: function (file) {
      if (this.$refs[this.refId].dropzone.multipleFilesError) {
        this.$refs[this.refId].dropzone.removeFile(file);
      }
    },
    progress: function (file, progress, bytesSent) {
      if (progress == 100) {
        this.setPersonalizeLoading(true);
      }
      this.setAttribute("loadingProgress", progress);
    },
    mounted: function () {
      this.prepareDropzoneInput();

      if (this.getIsMyAccount) {
        var uploadedFileTmp = this.getMyAccountUploadedFiles(
          this.propsData.code
        );
        if (uploadedFileTmp) {
          this.afterUploadFile(
            uploadedFileTmp,
            uploadedFileTmp.file,
            uploadedFileTmp.original_file_name,
            true
          );
        }
      } else {
        const pp_defaultSelection = this.getDefaultSelectionUploadsPP(
          this.propsData.code
        );
        if (pp_defaultSelection) {
          if (pp_defaultSelection.file_info.preflight) {
            // this info should come from first changeoptions in correct language
            delete pp_defaultSelection.file_info.preflight;
            this.setAttribute("showPreflight", true);
          }
          this.afterUploadFile(
            pp_defaultSelection.file_info,
            pp_defaultSelection.uploadedFile,
            pp_defaultSelection.file_url,
            true
          );
        }
      }

      if (this.getIsPackingEnabled) {
        window.document.addEventListener(
          "pqMatrix_paperChanged",
          this.onDelete
        );
      }
    },
    prepareDropzoneInput() {
      var dropzone = this.$refs[this.refId].dropzone;
      dropzone.hiddenFileInput.removeAttribute("multiple");
      dropzone.multipleFilesError = false;
      dropzone.hiddenFileInput.addEventListener("click", function (event) {
        dropzone.multipleFilesError = false;
      });
    },
    getAttribute: function (key) {
      return this.getUploadForProductPart(this.propsData.code)[key];
    },
    setAttribute: function (key, value) {
      return this.setAttributeUploadForProductPart({
        pp: this.propsData.code,
        key,
        value
      });
    },
    setAttributes: function (obj) {
      return this.setAttributeMultiUploadForProductPart({
        pp: this.propsData.code,
        obj
      });
    },
    onDelete: function (event) {
      this.uploadOnDelete(this.propsData.code);
    }
  },
  props: ["propsData", "excel"],
  data: function () {
    const urlsStore = useUrlsStore();
    const webproductStore = useWebproductStore();
    return {
      dropzoneOptions: {
        url: urlsStore.getUrl(axios.apiUpload),
        maxFilesize: webproductStore.getMaxUploadFileSize,
        dictDefaultMessage:
          i18n.t("Upload") + " " + i18n.t(PREFIX.PP + this.propsData.label),
        uploadMultiple: false,
        acceptedFiles: this.getAcceptedFiles().join(","),
        paramName: "qqfile",
        timeout: 0,
        previewTemplate:
          '<div id="template-preview"><div class="dz-preview dz-file-preview well" id="dz-preview-template"><div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div></div></div>'
      }
    };
  }
};
</script>
